import {ScaleLinear} from 'd3';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {Sequence} from 'components/Sequence';
import {PolymerLayout, SliceMapType} from 'constants/MapsConstants';
import PartsView from 'molbio/maps/linear/PartsView';
import {PART_NAME_FONT_SIZE} from 'molbio/maps/PartsViewConstants';
import {
  splitNucleotidePartModelsIntoHighlightRegions,
  PartHighlightRegion,
} from 'molbio/maps/splitPartsIntoHighlightRegions';
import Type from 'molbio/maps/Type';

function getPartRegionsForSlice(start: number, end: number, sequence: Sequence): Array<PartHighlightRegion> {
  const sequenceLength = sequence.length();
  // Sometimes, we have extra slices past the end of the sequence. This causes warnings when getting the
  // relevant parts, so we early return in those cases, and clamp the end such that start/end is always in
  // the [0, seqLength] range.
  if (start > sequenceLength) {
    return [];
  }
  const clampedEnd = Math.min(end, sequenceLength);
  const parts = sequence.parts().getSequencePartsBetween(start, clampedEnd);
  return splitNucleotidePartModelsIntoHighlightRegions(parts, sequence);
}

function getHeight(dnaHeight: number, dnaSpacer: number, dnaComplement: boolean): number {
  if (dnaComplement) {
    return 2 * (dnaHeight + dnaSpacer);
  } else {
    return dnaHeight + dnaSpacer;
  }
}

interface NucleotidePartsViewProps {
  /** A function that takes the index of a residue and returns its x-axis position in pixels on the slice */
  xFn: ScaleLinear<number, number>;
  /** The index of the bp at which the slice starts */
  sliceStart: number;
  /** The index of the bp at which the slice ends */
  sliceEnd: number;
  /** The Backbone sequence being rendered */
  sequence: Sequence;
  /** Flat object layout data */
  polymerLayout: PolymerLayout;
  /** Height in pixels of the DNA residues at default zoom. */
  dnaHeight: number;
  /** Height in pixels of the space below the DNA residues at default zoom. */
  dnaSpacer: number;
  /** Whether or not the complement slice is visible */
  complementVisible: boolean;
  /** What kind of map we're drawing on */
  mapType: SliceMapType;
  /**
   * A function that accepts a new box height or a way to calculate it and triggers a redraw with the new box
   * height.
   * */
  redraw: (newBoxHeight?: number | ((layout: PolymerLayout) => number)) => void;
}

export default function NucleotidePartsView({
  sliceStart,
  sliceEnd,
  sequence,
  dnaHeight,
  dnaSpacer,
  complementVisible,
  xFn,
  polymerLayout,
  mapType,
  redraw,
}: NucleotidePartsViewProps): JSX.Element {
  const partRegions = getPartRegionsForSlice(sliceStart, sliceEnd, sequence);
  const type = new Type(PART_NAME_FONT_SIZE);
  const boxHeight = type.height() + 2;

  return (
    <PartsView
      sliceStart={sliceStart}
      sliceEnd={sliceEnd}
      partRegions={partRegions}
      useOverlappingPartsStyling={BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS}
      sequenceLength={sequence.length()}
      boxHeight={boxHeight}
      height={getHeight(dnaHeight, dnaSpacer, complementVisible)}
      type={type}
      backgroundY={boxHeight}
      xFn={xFn}
      polymerLayout={polymerLayout}
      mapType={mapType}
      redraw={redraw}
    />
  );
}
